/**
 * Mover collision detection.
 *
 * Sub-millimetre accurate for the dominant XTS case: two movers travelling
 * along the same chain. Each mover occupies a 1D footprint of
 * `pathLengthMm` centred on `partPositionMm`, so the collision predicate
 * collapses to one subtraction in path-space:
 *
 *     centerDistance = arc-length |pos_a − pos_b|
 *     requiredGap    = (pathLength_a + pathLength_b) / 2
 *     penetration    = requiredGap − centerDistance
 *     collision iff  penetration > 0  (≥ 0 for "touching")
 *
 * For closed-loop chains (oval tracks, U-turn pairs) the arc-length is
 * taken as the *minimum* of the forward and the wrap-around path so two
 * movers near the seam (e.g. 50 mm and trackLength − 50 mm) are treated
 * as physically adjacent. Open chains are detected by comparing the
 * chain's start- and end-pose; for those the wrap-around distance is
 * simply not considered.
 *
 * Floating-point precision: positions are kept in mm in float64. A typical
 * XTS layout has trackLength ≤ 20 m → < 2×10⁴ mm, well inside the
 * 2⁵³-mantissa range, so subtractive cancellation costs at most ~10⁻¹²
 * mm — orders of magnitude below sub-millimetre.
 *
 * Multi-XPU (different chains): not handled here. Cross-track collisions
 * are dominated by track-transform layout and would need an OBB / mesh
 * test in 3D space; that's a different concern from "two movers on the
 * same line about to crash".
 */

import type { BuiltChain } from './ChainBuilder.js';
import type { MoverRef, MoverType3D } from '../types.js';
import { getMoverEntry } from './MoverCatalog.js';
import { isChainClosed } from './chainClosure.js';

/**
 * One detected pair. Result list is symmetric (a/b is reported once per
 * pair, with `a` carrying the lower index pair-canonical).
 */
export interface MoverCollision {
  a: MoverRef;
  b: MoverRef;
  /**
   * Stable IDs of the two movers (mirrors `MoverConfig.id`). Useful when
   * the consumer keys by id rather than by `moverIndex`.
   */
  idA: number;
  idB: number;
  /**
   * Penetration along the 1D path:
   *   • `> 0` — movers overlap by this many mm.
   *   • `= 0` — touching exactly.
   *   • `< 0` — closest-approach gap (only present in result when
   *             `warningGapMm` is set; the magnitude is the free-space
   *             distance between the closer footprint edges).
   */
  penetrationMm: number;
  /**
   * Both `partPositionMm` values at the time of the test. Lets the
   * consumer recompute or visualise without re-querying.
   */
  positionAMm: number;
  positionBMm: number;
  /** Mover lengths (catalog `pathLengthMm`). */
  pathLengthAMm: number;
  pathLengthBMm: number;
  /** True when measurement wrapped around the chain end (closed-loop seam). */
  viaWraparound: boolean;
}

export interface CheckMoverCollisionsOptions {
  /**
   * When `> 0`, near-misses with closest-approach gap up to this many mm
   * are also returned (with `penetrationMm < 0`). Default 0 — only actual
   * collisions / contacts.
   */
  warningGapMm?: number;
  /**
   * Force-treat the chain as closed (consider wrap-around distance) or
   * open (don't). When omitted, auto-detected by comparing
   * `chain.endPose` to `startPose ≈ identity` within ~0.1 mm tolerance.
   */
  forceClosedLoop?: boolean;
}

/**
 * Effective mover state passed into the predicate. Decoupled from
 * MoverConfig so consumers can plug in live positions from the
 * MoverPositionStore (60-Hz tick) without round-tripping through React
 * state.
 */
export interface MoverProbe {
  ref: MoverRef;
  id: number;
  partOid: number;
  moverType: MoverType3D;
  partPositionMm: number;
  /**
   * Override the catalog `pathLengthMm`. Use for `Custom` movers where
   * the layout's `pathLengthMm` is the source of truth.
   */
  pathLengthMm?: number;
}

const DEFAULT_WARNING_GAP_MM = 0;

/**
 * Pair-wise 1D collision test for movers on the same chain.
 *
 * `chain` is the BuiltChain corresponding to the partOid all probed
 * movers travel on. `probes` may contain movers from *any* part — this
 * function only considers pairs where `partOid` matches the chain.
 *
 * Returns one entry per colliding (or near-miss, when `warningGapMm > 0`)
 * pair, sorted by `penetrationMm` descending so the deepest collisions
 * appear first.
 */
export function checkSamePathCollisions(
  chain: BuiltChain,
  partOid: number,
  probes: ReadonlyArray<MoverProbe>,
  opts: CheckMoverCollisionsOptions = {},
): MoverCollision[] {
  const warningGapMm = opts.warningGapMm ?? DEFAULT_WARNING_GAP_MM;
  const closed = opts.forceClosedLoop ?? isChainClosed(chain);
  const trackLengthMm = chain.trackLengthMm;

  const onChain = probes.filter((p) => p.partOid === partOid);
  const out: MoverCollision[] = [];

  for (let i = 0; i < onChain.length; i++) {
    const a = onChain[i]!;
    const lenA =
      a.pathLengthMm ?? getMoverEntry(a.moverType)?.pathLengthMm ?? 0;
    if (lenA <= 0) continue;
    for (let j = i + 1; j < onChain.length; j++) {
      const b = onChain[j]!;
      const lenB =
        b.pathLengthMm ?? getMoverEntry(b.moverType)?.pathLengthMm ?? 0;
      if (lenB <= 0) continue;

      const linear = Math.abs(a.partPositionMm - b.partPositionMm);
      let centerDist = linear;
      let viaWraparound = false;
      if (closed) {
        const wrap = trackLengthMm - linear;
        if (wrap < linear) {
          centerDist = wrap;
          viaWraparound = true;
        }
      }
      const requiredGap = (lenA + lenB) / 2;
      const penetrationMm = requiredGap - centerDist;
      // `>= -warningGapMm` so an exact contact (penetrationMm === 0) is
      // always reported, regardless of warningGapMm.
      if (penetrationMm >= -warningGapMm) {
        out.push({
          a: a.ref,
          b: b.ref,
          idA: a.id,
          idB: b.id,
          penetrationMm,
          positionAMm: a.partPositionMm,
          positionBMm: b.partPositionMm,
          pathLengthAMm: lenA,
          pathLengthBMm: lenB,
          viaWraparound,
        });
      }
    }
  }

  out.sort((a, b) => b.penetrationMm - a.penetrationMm);
  return out;
}
